import React from 'react';
import { useSelector } from 'react-redux';

import { Col, Row, Space, Spin, Tag, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import moment from 'moment-timezone';
import * as LucideIcons from 'lucide-react';
import { isEmpty } from 'lodash';

import {
  AnimalActivityPayload,
  AutomobileLossClaimEventPayload,
  DirectBillAmountEventPayload,
  DirectBillAmountPaymentEventPayload,
  DriverActivityPayload,
  EndorsementActivityPayload,
  EndorsementPayableEventPayload,
  EndorsementReceivablePaymentEventPayload,
  EndorsementPayablePaymentEventPayload,
  EquipmentActivityPayload,
  GeneralLiabilityClaimEventPayload,
  GroupHealthMemberActivityPayload,
  HoaUnitActivityPayload,
  ImportantDateActivityPayload,
  LifeInsuranceActivityPayload,
  MasterCertificateActivityPayload,
  MotorTruckCargoActivityPayload,
  NoteEventPayload,
  OtherClaimEventPayload,
  PolicyEventPayload,
  ProfessionalLiabilityActivityPayload,
  PropertyActivityPayload,
  PropertyLossClaimEventPayload,
  ServiceRequestAddDriverEventPayload,
  ServiceRequestAddressChangeEventPayload,
  ServiceRequestGeneralNewEventPayload,
  ServiceRequestGenericEventPayload,
  ServiceRequestRemoveDriverEventPayload,
  ServiceRequestReplaceDriverEventPayload,
  ServiceRequestVehicleTransferEventPayload,
  TagApplyEventPayload,
  TagApplyOnPolicyEventPayload,
  TaskEventPayload,
  TodoEventPayload,
  TruckingCompanyEventPayload,
  TruckingCompanyOpportunityEventPayload,
  UserActivityEvent,
  VehicleActivityPayload,
  WatercraftActivityPayload,
  WorkerCompensationClaimEventPayload,
  XDateEventPayload,
  PendingCancellationEventPayload,
  FinanceAgreementEventPayload,
  MedicalHealthClaimEventPayload,
  HistoricalCertificateHolderEventPayload,
  InsuredInvoiceEventPayload,
  FloodCoverageEventPayload,
  ServiceRequestAddVehicleEventPayload,
  ServiceRequestCertificateOfEventPayload,
  PrincipalEventPayload,
  EndorsementReceivableEventPayload,
  EndorsementFinancingEventPayload,
  EndorsementFinancingPaymentEventPayload,
  EndorsementFinancingIncentiveEventPayload,
  EndorsementFinancingIncentivePaymentEventPayload,
  EndorsementAgentsCommissionPaymentEventPayload,
  EndorsementAgencyCommissionPaymentEventPayload,
  EndorsementAgentCommissionEventPayload,
  EndorsementAgencyCommissionEventPayload,
  TagAppliesToOpportunityEventPayload,
  WorkgroupEventPayload,
  ReferralSourceCategoryEventPayload,
  CertificateHolderEventPayload,
  TaskCategoryEventPayload,
  LOBEventPayload,
  OpportunityStageEventPayload,
  InsuranceAgencyEventPayload,
  AgentEventPayload,
  TagEventPayload,
  OpportunityTrackingKeysEventPayload,
} from './models';
import { RootState } from '../../store';
import { EventObjectTypeEnum, VendorPortalEvents } from '../../shared/constants/enums';

const eventTypeIconMap: { [key: string]: keyof typeof LucideIcons } = {
  POLICY: 'FileText',
  QUOTE: 'FileText',
  INSURED: 'UserCheck',
  PROSPECT: 'UserPlus',
  REFERRAL: 'Share2',
  REFERRALSOURCE: 'Share',
  MGA: 'Building2',
  CARRIER: 'Truck',
  FINANCECOMPANY: 'Banknote',
  OTHERTRUCKINGCOMPANY: 'Truck',
  CONVERT_PROSPECT_TO_INSURED: 'ArrowRightLeft',
  NOTE: 'StickyNote',
  TASK: 'ClipboardList',
  TODO: 'ListTodo',
  DRIVER: 'Car',
  VEHICLE: 'Truck',
  PROPERTY: 'Home',
  EQUIPMENT: 'Wrench',
  PROFESSIONAL_LIABILITY: 'ShieldCheck',
  MOTOR_TRUCK_CARGO: 'Package',
  HOA_UNIT: 'Building',
  GROUP_HEALTH_MEMBER: 'User',
  WATERCRAFT: 'Ship',
  LIFE_INS: 'HeartPulse',
  ANIMAL: 'Dog',
  IMPORTANT_DATE: 'CalendarDays',
  ENDORSEMENT: 'FileSignature',
  MASTERCERTIFICATE: 'Award',
  TRUCKINGCOMPANYOPPORTUNITY: 'Briefcase',
  SERVICEREQUESTADDDRIVER: 'UserPlus',
  SERVICEREQUESTADDRESSCHANGE: 'MapPin',
  SERVICEREQUESTREMOVEDRIVER: 'UserMinus',
  SERVICEREQUESTREPLACEDRIVER: 'UserCheck',
  SERVICEREQUESTVEHICLETRANSFER: 'Repeat',
  SERVICEREQUESTREPLACEVEHICLE: 'RefreshCw',
  SERVICEREQUESTGENERALNEW: 'FilePlus',
  SERVICEREQUESTGENERIC: 'File',
  SERVICEREQUESTPOLICYCHANGE: 'FileEdit',
  SERVICEREQUESTADDVEHICLE: 'CarFront',
  PRINCIPAL: 'UserCog',
  SERVICEREQUESTCERTIFICATE_OF: 'Award',
  OTHERCLAIM: 'AlertCircle',
  TAGAPPLY: 'Tag',
  TAGAPPLYONPOLICY: 'Tag',
  XDate: 'CalendarClock',
  PENDINGCANCELLATION: 'PauseCircle',
  FINANCE_AGREEMENT: 'FileText',
  MEDICALHEALTHCLAIM: 'Stethoscope',
  HISTORICAL_CERTIFICATE_HOLDER: 'Award',
  CERTIFICATE_HOLDER: 'Award',
  TASK_CATEGORY: 'ClipboardList',
  LOB: 'Layers',
  OPPORTUNITYSTAGE: 'TrendingUp',
  INSURANCEAGENCY: 'Building2',
  AGENT: 'User',
  TAG: 'Tag',
  DIRECT_BILL_AMOUNT: 'DollarSign',
  DIRECT_BILL_AMOUNT_PAYMENT: 'DollarSign',
  INSURED_INVOICE: 'Receipt',
  FLOOD_COVERAGE: 'Droplets',
  ENDORSEMENT_RECEIVABLE: 'FileSignature',
  ENDORSEMENT_RECEIVABLE_PAYMENT: 'FileSignature',
  ENDORSEMENT_PAYABLE: 'FileSignature',
  ENDORSEMENT_PAYABLE_PAYMENT: 'FileSignature',
  ENDORSEMENT_FINANCING: 'FileSignature',
  ENDORSEMENT_FINANCING_PAYMENT: 'FileSignature',
  ENDORSEMENT_FINANCING_INCENTIVE: 'Gift',
  ENDORSEMENT_FINANCING_INCENTIVE_PAYMENT: 'Gift',
  ENDORSEMENT_AGENTS_COMMISSION_PAYMENT: 'UserCheck',
  ENDORSEMENT_AGENCY_COMMISSION_PAYMENT: 'UserCheck',
  ENDORSEMENT_AGENT_COMMISSION: 'UserCheck',
  ENDORSEMENT_AGENCY_COMMISSION: 'UserCheck',
  TAGAPPLIESTOOPPORTUNITY: 'Tag',
  WORKGROUP: 'Users',
  REFERRAL_SOURCE_CATEGORY: 'Bookmark',
  POLICY_STATUS_CHANGED: 'FileText',
};

type UserActivityEventItemProps = {
  userActivityEvent: UserActivityEvent;
  objectType: EventObjectTypeEnum;
};

const UserActivityEventItem: React.FC<UserActivityEventItemProps> = (props) => {
  const { humanReadableEventTypes, humanReadableEventTypesLoading, userTimeZone, userTimeZoneLoading } = useSelector(
    (state: RootState) => state.userActivityEvents,
  );

  const { token } = theme.useToken();

  function getEventIcon(eventType: string) {
    const baseType = eventType.replace(/_(INSERT|UPDATE|DELETE)$/i, '');
    const iconName = eventTypeIconMap[baseType] || 'Circle';
    const IconComponent = LucideIcons[iconName] as React.ElementType | undefined;
    if (IconComponent) {
      return <IconComponent color={token.colorPrimary} size={20} />;
    }
    return null;
  }

  const convertToUserTimeZone = (date: string | Date | undefined | null) => {
    if (!date) return '';
    if (!userTimeZone) return moment.tz(date, 'America/Chicago').format('MM/DD/YYYY h:mm:ss A'); // Central Standard Time
    return moment.tz(date, 'America/Chicago').tz(userTimeZone).format('MM/DD/YYYY h:mm:ss A');
  };

  const eventDescription = (): React.ReactNode => {
    let description = <></>;
    switch (props.userActivityEvent.eventType) {
      case VendorPortalEvents.POLICY_DELETE:
      case VendorPortalEvents.POLICY_INSERT:
      case VendorPortalEvents.POLICY_UPDATE:
      case VendorPortalEvents.QUOTE_DELETE:
      case VendorPortalEvents.QUOTE_INSERT:
      case VendorPortalEvents.QUOTE_UPDATE:
      case VendorPortalEvents.POLICY_STATUS_CHANGED:
        let policyEventPayload = (props.userActivityEvent.payloadAfter || {}) as PolicyEventPayload;
        description = (
          <>
            {props.objectType !== EventObjectTypeEnum.Policy && policyEventPayload?.number && (
              <>
                Policy: {policyEventPayload.number}
                <br />
              </>
            )}

            {policyEventPayload?.status && (
              <>
                Status: {policyEventPayload.status}
                <br />
              </>
            )}

            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (policyEventPayload.insuredCommercialName || policyEventPayload.insuredFirstName || policyEventPayload.insuredLastName) && (
                <>
                  {policyEventPayload.truckingCompanyType && !isEmpty(policyEventPayload.truckingCompanyType)
                    ? policyEventPayload.truckingCompanyType + ': '
                    : null}
                  {policyEventPayload.insuredCommercialName ??
                    [policyEventPayload.insuredFirstName, policyEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </>
        );
        break;

      case VendorPortalEvents.INSURED_DELETE:
      case VendorPortalEvents.INSURED_INSERT:
      case VendorPortalEvents.INSURED_UPDATE:
      case VendorPortalEvents.PROSPECT_DELETE:
      case VendorPortalEvents.PROSPECT_INSERT:
      case VendorPortalEvents.PROSPECT_UPDATE:
      case VendorPortalEvents.REFERRAL_DELETE:
      case VendorPortalEvents.REFERRAL_INSERT:
      case VendorPortalEvents.REFERRAL_UPDATE:
      case VendorPortalEvents.REFERRALSOURCE_DELETE:
      case VendorPortalEvents.REFERRALSOURCE_INSERT:
      case VendorPortalEvents.REFERRALSOURCE_UPDATE:
      case VendorPortalEvents.MGA_DELETE:
      case VendorPortalEvents.MGA_INSERT:
      case VendorPortalEvents.MGA_UPDATE:
      case VendorPortalEvents.CARRIER_DELETE:
      case VendorPortalEvents.CARRIER_INSERT:
      case VendorPortalEvents.CARRIER_UPDATE:
      case VendorPortalEvents.FINANCECOMPANY_DELETE:
      case VendorPortalEvents.FINANCECOMPANY_INSERT:
      case VendorPortalEvents.FINANCECOMPANY_UPDATE:
      case VendorPortalEvents.OTHERTRUCKINGCOMPANY_DELETE:
      case VendorPortalEvents.OTHERTRUCKINGCOMPANY_INSERT:
      case VendorPortalEvents.OTHERTRUCKINGCOMPANY_UPDATE:
      case VendorPortalEvents.CONVERT_PROSPECT_TO_INSURED:
        let insuredEventPayload = (props.userActivityEvent.payloadAfter || {}) as TruckingCompanyEventPayload;
        description = (
          <div>
            {insuredEventPayload.type && !isEmpty(insuredEventPayload.type) ? insuredEventPayload.type + ': ' : null}
            {insuredEventPayload.commercialName ?? [insuredEventPayload.firstName, insuredEventPayload.lastName].filter(Boolean).join(' ')}
            <br />
          </div>
        );
        break;

      case VendorPortalEvents.NOTE_DELETE:
      case VendorPortalEvents.NOTE_INSERT:
      case VendorPortalEvents.NOTE_UPDATE:
        let noteEventPayload = (props.userActivityEvent.payloadAfter || {}) as NoteEventPayload;
        description = (
          <div>
            {noteEventPayload.subject && (
              <>
                Subject: {noteEventPayload.subject.length > 100 ? noteEventPayload.subject.slice(0, 100) + '...' : noteEventPayload.subject}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (noteEventPayload.insuredCommercialName || noteEventPayload.insuredFirstName || noteEventPayload.insuredLastName) && (
                <>
                  {noteEventPayload.truckingCompanyType && !isEmpty(noteEventPayload.truckingCompanyType)
                    ? noteEventPayload.truckingCompanyType + ': '
                    : null}
                  {noteEventPayload.insuredCommercialName ??
                    [noteEventPayload.insuredFirstName, noteEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.TASK_DELETE:
      case VendorPortalEvents.TASK_INSERT:
      case VendorPortalEvents.TASK_UPDATE:
        let taskEventPayload = (props.userActivityEvent.payloadAfter || {}) as TaskEventPayload;
        description = (
          <div>
            {taskEventPayload.creatorName && (
              <>
                Creator: {taskEventPayload.creatorName}
                <br />
              </>
            )}
            {taskEventPayload.title && (
              <>
                Title: {taskEventPayload.title.length > 100 ? taskEventPayload.title.slice(0, 100) + '...' : taskEventPayload.title}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (taskEventPayload.insuredCommercialName || taskEventPayload.insuredFirstName || taskEventPayload.insuredLastName) && (
                <>
                  {taskEventPayload.truckingCompanyType && !isEmpty(taskEventPayload.truckingCompanyType)
                    ? taskEventPayload.truckingCompanyType + ': '
                    : null}
                  {taskEventPayload.insuredCommercialName ??
                    [taskEventPayload.insuredFirstName, taskEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.TODO_DELETE:
      case VendorPortalEvents.TODO_INSERT:
      case VendorPortalEvents.TODO_UPDATE:
        let todoEventPayload = (props.userActivityEvent.payloadAfter || {}) as TodoEventPayload;
        description = (
          <div>
            {todoEventPayload.title && (
              <>
                Title: {todoEventPayload.title.length > 100 ? todoEventPayload.title.slice(0, 100) + '...' : todoEventPayload.title}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (todoEventPayload.insuredCommercialName || todoEventPayload.insuredFirstName || todoEventPayload.insuredLastName) && (
                <>
                  {todoEventPayload.truckingCompanyType && !isEmpty(todoEventPayload.truckingCompanyType)
                    ? todoEventPayload.truckingCompanyType + ': '
                    : null}
                  {todoEventPayload.insuredCommercialName ??
                    [todoEventPayload.insuredFirstName, todoEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.DRIVER_DELETE:
      case VendorPortalEvents.DRIVER_INSERT:
      case VendorPortalEvents.DRIVER_UPDATE:
        let driverEventPayload = (props.userActivityEvent.payloadAfter || {}) as DriverActivityPayload;
        description = (
          <div>
            {(driverEventPayload.firstName || driverEventPayload.lastName) && (
              <>
                Driver: {[driverEventPayload.firstName, driverEventPayload.lastName].filter(Boolean).join(' ')}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (driverEventPayload.insuredCommercialName || driverEventPayload.insuredFirstName || driverEventPayload.insuredLastName) && (
                <>
                  {driverEventPayload.truckingCompanyType && !isEmpty(driverEventPayload.truckingCompanyType)
                    ? driverEventPayload.truckingCompanyType + ': '
                    : null}
                  {driverEventPayload.insuredCommercialName ??
                    [driverEventPayload.insuredFirstName, driverEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.VEHICLE_DELETE:
      case VendorPortalEvents.VEHICLE_INSERT:
      case VendorPortalEvents.VEHICLE_UPDATE:
        let vehicleEventPayload = (props.userActivityEvent.payloadAfter || {}) as VehicleActivityPayload;
        description = (
          <div>
            {vehicleEventPayload.make && (
              <>
                Make: {vehicleEventPayload.make}
                <br />
              </>
            )}
            {vehicleEventPayload.model && (
              <>
                Model: {vehicleEventPayload.model}
                <br />
              </>
            )}
            {vehicleEventPayload.year && (
              <>
                Year: {vehicleEventPayload.year}
                <br />
              </>
            )}
            {vehicleEventPayload.vIN && (
              <>
                VIN: {vehicleEventPayload.vIN}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (vehicleEventPayload.insuredCommercialName || vehicleEventPayload.insuredFirstName || vehicleEventPayload.insuredLastName) && (
                <>
                  {vehicleEventPayload.truckingCompanyType && !isEmpty(vehicleEventPayload.truckingCompanyType)
                    ? vehicleEventPayload.truckingCompanyType + ': '
                    : null}
                  {vehicleEventPayload.insuredCommercialName ??
                    [vehicleEventPayload.insuredFirstName, vehicleEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.PROPERTY_DELETE:
      case VendorPortalEvents.PROPERTY_INSERT:
      case VendorPortalEvents.PROPERTY_UPDATE:
        let propertyEventPayload = (props.userActivityEvent.payloadAfter || {}) as PropertyActivityPayload;
        description = (
          <div>
            {propertyEventPayload.description && (
              <>
                Description: {propertyEventPayload.description}
                <br />
              </>
            )}
            {(propertyEventPayload.locationNumber ||
              propertyEventPayload.addressLine1 ||
              propertyEventPayload.addressLine2 ||
              propertyEventPayload.city ||
              propertyEventPayload.state ||
              propertyEventPayload.zip) && (
              <>
                Address:{' '}
                {[
                  propertyEventPayload.locationNumber,
                  propertyEventPayload.addressLine1,
                  propertyEventPayload.addressLine2,
                  propertyEventPayload.city,
                  propertyEventPayload.state,
                  propertyEventPayload.zip,
                ]
                  .filter(Boolean)
                  .join(',')}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (propertyEventPayload.insuredCommercialName || propertyEventPayload.insuredFirstName || propertyEventPayload.insuredLastName) && (
                <>
                  {propertyEventPayload.truckingCompanyType && !isEmpty(propertyEventPayload.truckingCompanyType)
                    ? propertyEventPayload.truckingCompanyType + ': '
                    : null}
                  {propertyEventPayload.insuredCommercialName ??
                    [propertyEventPayload.insuredFirstName, propertyEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.EQUIPMENT_DELETE:
      case VendorPortalEvents.EQUIPMENT_INSERT:
      case VendorPortalEvents.EQUIPMENT_UPDATE:
        let equipmentEventPayload = (props.userActivityEvent.payloadAfter || {}) as EquipmentActivityPayload;
        description = (
          <div>
            {equipmentEventPayload.unitNumber && (
              <>
                Unit Number: {equipmentEventPayload.unitNumber}
                <br />
              </>
            )}
            {equipmentEventPayload.idSerialNumber && (
              <>
                ID/Serial Number: {equipmentEventPayload.idSerialNumber}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (equipmentEventPayload.insuredCommercialName || equipmentEventPayload.insuredFirstName || equipmentEventPayload.insuredLastName) && (
                <>
                  {equipmentEventPayload.truckingCompanyType && !isEmpty(equipmentEventPayload.truckingCompanyType)
                    ? equipmentEventPayload.truckingCompanyType + ': '
                    : null}
                  {equipmentEventPayload.insuredCommercialName ??
                    [equipmentEventPayload.insuredFirstName, equipmentEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.PROFESSIONAL_LIABILITY_DELETE:
      case VendorPortalEvents.PROFESSIONAL_LIABILITY_INSERT:
      case VendorPortalEvents.PROFESSIONAL_LIABILITY_UPDATE:
        let professionalLiabilityEventPayload = (props.userActivityEvent.payloadAfter || {}) as ProfessionalLiabilityActivityPayload;
        description = (
          <div>
            {(professionalLiabilityEventPayload.firstName || professionalLiabilityEventPayload.lastName) && (
              <>
                Name: {[professionalLiabilityEventPayload.firstName, professionalLiabilityEventPayload.lastName].filter(Boolean).join(' ')}
                <br />
              </>
            )}
            {professionalLiabilityEventPayload.title && (
              <>
                Title: {professionalLiabilityEventPayload.title}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (professionalLiabilityEventPayload.insuredCommercialName ||
                professionalLiabilityEventPayload.insuredFirstName ||
                professionalLiabilityEventPayload.insuredLastName) && (
                <>
                  {professionalLiabilityEventPayload.truckingCompanyType && !isEmpty(professionalLiabilityEventPayload.truckingCompanyType)
                    ? professionalLiabilityEventPayload.truckingCompanyType + ': '
                    : null}
                  {professionalLiabilityEventPayload.insuredCommercialName ??
                    [professionalLiabilityEventPayload.insuredFirstName, professionalLiabilityEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.MOTOR_TRUCK_CARGO_DELETE:
      case VendorPortalEvents.MOTOR_TRUCK_CARGO_INSERT:
      case VendorPortalEvents.MOTOR_TRUCK_CARGO_UPDATE:
        let motorTruckCargoEventPayload = (props.userActivityEvent.payloadAfter || {}) as MotorTruckCargoActivityPayload;
        description = (
          <div>
            {motorTruckCargoEventPayload.commoditiesHauled && (
              <>
                Commodities Hauled: {motorTruckCargoEventPayload.commoditiesHauled}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (motorTruckCargoEventPayload.insuredCommercialName ||
                motorTruckCargoEventPayload.insuredFirstName ||
                motorTruckCargoEventPayload.insuredLastName) && (
                <>
                  {motorTruckCargoEventPayload.truckingCompanyType && !isEmpty(motorTruckCargoEventPayload.truckingCompanyType)
                    ? motorTruckCargoEventPayload.truckingCompanyType + ': '
                    : null}
                  {motorTruckCargoEventPayload.insuredCommercialName ??
                    [motorTruckCargoEventPayload.insuredFirstName, motorTruckCargoEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.HOA_UNIT_DELETE:
      case VendorPortalEvents.HOA_UNIT_INSERT:
      case VendorPortalEvents.HOA_UNIT_UPDATE:
        let hoaUnitEventPayload = (props.userActivityEvent.payloadAfter || {}) as HoaUnitActivityPayload;
        description = (
          <div>
            {hoaUnitEventPayload.unitNumber && (
              <>
                HOA Unit Number: {hoaUnitEventPayload.unitNumber}
                <br />
              </>
            )}
            {hoaUnitEventPayload.ownerVesting && (
              <>
                Owner Vesting: {hoaUnitEventPayload.ownerVesting}
                <br />
              </>
            )}
            {hoaUnitEventPayload.loanNumber && (
              <>
                Loan Number: {hoaUnitEventPayload.loanNumber}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (hoaUnitEventPayload.insuredCommercialName || hoaUnitEventPayload.insuredFirstName || hoaUnitEventPayload.insuredLastName) && (
                <>
                  {hoaUnitEventPayload.truckingCompanyType && !isEmpty(hoaUnitEventPayload.truckingCompanyType)
                    ? hoaUnitEventPayload.truckingCompanyType + ': '
                    : null}
                  {hoaUnitEventPayload.insuredCommercialName ??
                    [hoaUnitEventPayload.insuredFirstName, hoaUnitEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.GROUP_HEALTH_MEMBER_DELETE:
      case VendorPortalEvents.GROUP_HEALTH_MEMBER_INSERT:
      case VendorPortalEvents.GROUP_HEALTH_MEMBER_UPDATE:
        let groupHealthMemberEventPayload = (props.userActivityEvent.payloadAfter || {}) as GroupHealthMemberActivityPayload;
        description = (
          <div>
            {(groupHealthMemberEventPayload.firstName || groupHealthMemberEventPayload.lastName) && (
              <>
                Name: {[groupHealthMemberEventPayload.firstName, groupHealthMemberEventPayload.lastName].filter(Boolean).join(' ')}
                <br />
              </>
            )}
            {groupHealthMemberEventPayload.citizenship && (
              <>
                Citizenship: {groupHealthMemberEventPayload.citizenship}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (groupHealthMemberEventPayload.insuredCommercialName ||
                groupHealthMemberEventPayload.insuredFirstName ||
                groupHealthMemberEventPayload.insuredLastName) && (
                <>
                  {groupHealthMemberEventPayload.truckingCompanyType && !isEmpty(groupHealthMemberEventPayload.truckingCompanyType)
                    ? groupHealthMemberEventPayload.truckingCompanyType + ': '
                    : null}
                  {groupHealthMemberEventPayload.insuredCommercialName ??
                    [groupHealthMemberEventPayload.insuredFirstName, groupHealthMemberEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.WATERCRAFT_DELETE:
      case VendorPortalEvents.WATERCRAFT_INSERT:
      case VendorPortalEvents.WATERCRAFT_UPDATE:
        let watercraftEventPayload = (props.userActivityEvent.payloadAfter || {}) as WatercraftActivityPayload;
        description = (
          <div>
            {watercraftEventPayload.model && (
              <>
                Model: {watercraftEventPayload.model}
                <br />
              </>
            )}
            {watercraftEventPayload.modelYear && (
              <>
                Model Year: {watercraftEventPayload.modelYear}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (watercraftEventPayload.insuredCommercialName || watercraftEventPayload.insuredFirstName || watercraftEventPayload.insuredLastName) && (
                <>
                  {watercraftEventPayload.truckingCompanyType && !isEmpty(watercraftEventPayload.truckingCompanyType)
                    ? watercraftEventPayload.truckingCompanyType + ': '
                    : null}
                  {watercraftEventPayload.insuredCommercialName ??
                    [watercraftEventPayload.insuredFirstName, watercraftEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.LIFE_INS_DELETE:
      case VendorPortalEvents.LIFE_INS_INSERT:
      case VendorPortalEvents.LIFE_INS_UPDATE:
        let lifeInsuranceEventPayload = (props.userActivityEvent.payloadAfter || {}) as LifeInsuranceActivityPayload;
        description = (
          <div>
            {lifeInsuranceEventPayload.firstName || lifeInsuranceEventPayload.lastName ? (
              <>
                Name: {[lifeInsuranceEventPayload.firstName, lifeInsuranceEventPayload.lastName].filter(Boolean).join(' ')}
                <br />
              </>
            ) : null}
            {lifeInsuranceEventPayload.type ? (
              <>
                Type: {lifeInsuranceEventPayload.type}
                <br />
              </>
            ) : null}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (lifeInsuranceEventPayload.insuredCommercialName ||
                lifeInsuranceEventPayload.insuredFirstName ||
                lifeInsuranceEventPayload.insuredLastName) && (
                <>
                  {lifeInsuranceEventPayload.truckingCompanyType && !isEmpty(lifeInsuranceEventPayload.truckingCompanyType)
                    ? lifeInsuranceEventPayload.truckingCompanyType + ': '
                    : null}
                  {lifeInsuranceEventPayload.insuredCommercialName ??
                    [lifeInsuranceEventPayload.insuredFirstName, lifeInsuranceEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ANIMAL_DELETE:
      case VendorPortalEvents.ANIMAL_INSERT:
      case VendorPortalEvents.ANIMAL_UPDATE:
        let animalEventPayload = (props.userActivityEvent.payloadAfter || {}) as AnimalActivityPayload;
        description = (
          <div>
            {animalEventPayload.name && (
              <>
                Animal Name: {animalEventPayload.name}
                <br />
              </>
            )}
            {animalEventPayload.sire && (
              <>
                Sire: {animalEventPayload.sire}
                <br />
              </>
            )}
            {animalEventPayload.dam && (
              <>
                Dam: {animalEventPayload.dam}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (animalEventPayload.insuredCommercialName || animalEventPayload.insuredFirstName || animalEventPayload.insuredLastName) && (
                <>
                  {animalEventPayload.truckingCompanyType && !isEmpty(animalEventPayload.truckingCompanyType)
                    ? animalEventPayload.truckingCompanyType + ': '
                    : null}
                  {animalEventPayload.insuredCommercialName ??
                    [animalEventPayload.insuredFirstName, animalEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.IMPORTANT_DATE_DELETE:
      case VendorPortalEvents.IMPORTANT_DATE_INSERT:
      case VendorPortalEvents.IMPORTANT_DATE_UPDATE:
        let importantDateEventPayload = (props.userActivityEvent.payloadAfter || {}) as ImportantDateActivityPayload;
        description = (
          <div>
            {importantDateEventPayload.importantDate && (
              <>
                Date: {convertToUserTimeZone(importantDateEventPayload.importantDate)} {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (importantDateEventPayload.insuredCommercialName ||
                importantDateEventPayload.insuredFirstName ||
                importantDateEventPayload.insuredLastName) && (
                <>
                  {importantDateEventPayload.truckingCompanyType && !isEmpty(importantDateEventPayload.truckingCompanyType)
                    ? importantDateEventPayload.truckingCompanyType + ': '
                    : null}
                  {importantDateEventPayload.insuredCommercialName ??
                    [importantDateEventPayload.insuredFirstName, importantDateEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_DELETE:
      case VendorPortalEvents.ENDORSEMENT_INSERT:
      case VendorPortalEvents.ENDORSEMENT_UPDATE:
        let endorsementEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementActivityPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementEventPayload.policyNumber && (
              <>
                Policy: {endorsementEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementEventPayload.amount && (
              <>
                Amount: {endorsementEventPayload.amount}
                <br />
              </>
            )}
            {endorsementEventPayload.description && (
              <>
                Description: {endorsementEventPayload.description}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementEventPayload.insuredCommercialName ||
                endorsementEventPayload.insuredFirstName ||
                endorsementEventPayload.insuredLastName) && (
                <>
                  {endorsementEventPayload.truckingCompanyType && !isEmpty(endorsementEventPayload.truckingCompanyType)
                    ? endorsementEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementEventPayload.insuredCommercialName ??
                    [endorsementEventPayload.insuredFirstName, endorsementEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.MASTERCERTIFICATE_DELETE:
      case VendorPortalEvents.MASTERCERTIFICATE_INSERT:
      case VendorPortalEvents.MASTERCERTIFICATE_UPDATE:
        let masterCertificateEventPayload = (props.userActivityEvent.payloadAfter || {}) as MasterCertificateActivityPayload;
        description = (
          <div>
            {masterCertificateEventPayload?.name && (
              <>
                Certificate: {masterCertificateEventPayload.name}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (masterCertificateEventPayload.insuredCommercialName ||
                masterCertificateEventPayload.insuredFirstName ||
                masterCertificateEventPayload.insuredLastName) && (
                <>
                  {masterCertificateEventPayload.truckingCompanyType && !isEmpty(masterCertificateEventPayload.truckingCompanyType)
                    ? masterCertificateEventPayload.truckingCompanyType + ': '
                    : null}
                  {masterCertificateEventPayload.insuredCommercialName ??
                    [masterCertificateEventPayload.insuredFirstName, masterCertificateEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.TRUCKINGCOMPANYOPPORTUNITY_DELETE:
      case VendorPortalEvents.TRUCKINGCOMPANYOPPORTUNITY_INSERT:
      case VendorPortalEvents.TRUCKINGCOMPANYOPPORTUNITY_UPDATE:
        let truckingCompanyOpportunityEventPayload = (props.userActivityEvent.payloadAfter || {}) as TruckingCompanyOpportunityEventPayload;
        description = (
          <div>
            {truckingCompanyOpportunityEventPayload.lineOfBusinessName && (
              <>
                Line of Business: {truckingCompanyOpportunityEventPayload.lineOfBusinessName}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (truckingCompanyOpportunityEventPayload.insuredCommercialName ||
                truckingCompanyOpportunityEventPayload.insuredFirstName ||
                truckingCompanyOpportunityEventPayload.insuredLastName) && (
                <>
                  {truckingCompanyOpportunityEventPayload.truckingCompanyType && !isEmpty(truckingCompanyOpportunityEventPayload.truckingCompanyType)
                    ? truckingCompanyOpportunityEventPayload.truckingCompanyType + ': '
                    : null}
                  {truckingCompanyOpportunityEventPayload.insuredCommercialName ??
                    [truckingCompanyOpportunityEventPayload.insuredFirstName, truckingCompanyOpportunityEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.SERVICEREQUESTADDDRIVER_DELETE:
      case VendorPortalEvents.SERVICEREQUESTADDDRIVER_INSERT:
      case VendorPortalEvents.SERVICEREQUESTADDDRIVER_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTADDRESSCHANGE_DELETE:
      case VendorPortalEvents.SERVICEREQUESTADDRESSCHANGE_INSERT:
      case VendorPortalEvents.SERVICEREQUESTADDRESSCHANGE_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTREMOVEDRIVER_DELETE:
      case VendorPortalEvents.SERVICEREQUESTREMOVEDRIVER_INSERT:
      case VendorPortalEvents.SERVICEREQUESTREMOVEDRIVER_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTREPLACEDRIVER_DELETE:
      case VendorPortalEvents.SERVICEREQUESTREPLACEDRIVER_INSERT:
      case VendorPortalEvents.SERVICEREQUESTREPLACEDRIVER_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTVEHICLETRANSFER_DELETE:
      case VendorPortalEvents.SERVICEREQUESTVEHICLETRANSFER_INSERT:
      case VendorPortalEvents.SERVICEREQUESTVEHICLETRANSFER_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTREPLACEVEHICLE_DELETE:
      case VendorPortalEvents.SERVICEREQUESTREPLACEVEHICLE_INSERT:
      case VendorPortalEvents.SERVICEREQUESTREPLACEVEHICLE_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTGENERALNEW_DELETE:
      case VendorPortalEvents.SERVICEREQUESTGENERALNEW_INSERT:
      case VendorPortalEvents.SERVICEREQUESTGENERALNEW_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTGENERIC_DELETE:
      case VendorPortalEvents.SERVICEREQUESTGENERIC_INSERT:
      case VendorPortalEvents.SERVICEREQUESTPOLICYCHANGE_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTPOLICYCHANGE_INSERT:
      case VendorPortalEvents.SERVICEREQUESTPOLICYCHANGE_DELETE:
      case VendorPortalEvents.SERVICEREQUESTADDVEHICLE_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTADDVEHICLE_INSERT:
      case VendorPortalEvents.SERVICEREQUESTADDVEHICLE_DELETE:
        let serviceRequestAddDriverEventPayload = (props.userActivityEvent.payloadAfter || {}) as
          | ServiceRequestAddDriverEventPayload
          | ServiceRequestAddressChangeEventPayload
          | ServiceRequestRemoveDriverEventPayload
          | ServiceRequestReplaceDriverEventPayload
          | ServiceRequestVehicleTransferEventPayload
          | ServiceRequestGeneralNewEventPayload
          | ServiceRequestGenericEventPayload
          | ServiceRequestAddVehicleEventPayload;
        description = (
          <div>
            {serviceRequestAddDriverEventPayload.description && (
              <>
                Description: {serviceRequestAddDriverEventPayload.description}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (serviceRequestAddDriverEventPayload.insuredCommercialName ||
                serviceRequestAddDriverEventPayload.insuredFirstName ||
                serviceRequestAddDriverEventPayload.insuredLastName) && (
                <>
                  {serviceRequestAddDriverEventPayload.truckingCompanyType && !isEmpty(serviceRequestAddDriverEventPayload.truckingCompanyType)
                    ? serviceRequestAddDriverEventPayload.truckingCompanyType + ': '
                    : null}
                  {serviceRequestAddDriverEventPayload.insuredCommercialName ??
                    [serviceRequestAddDriverEventPayload.insuredFirstName, serviceRequestAddDriverEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.PRINCIPAL_DELETE:
      case VendorPortalEvents.PRINCIPAL_INSERT:
      case VendorPortalEvents.PRINCIPAL_UPDATE:
        let principalEventPayload = (props.userActivityEvent.payloadAfter || {}) as PrincipalEventPayload;
        description = (
          <div>
            {principalEventPayload &&
              [principalEventPayload.firstName, principalEventPayload.middleName, principalEventPayload.lastName].filter(Boolean).length > 0 && (
                <>
                  Principal:{' '}
                  {[principalEventPayload.firstName, principalEventPayload.middleName, principalEventPayload.lastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
            {principalEventPayload?.description && (
              <>
                Description: {principalEventPayload.description}
                <br />
              </>
            )}
            {principalEventPayload?.type && (
              <>
                Type: {principalEventPayload.type}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany && principalEventPayload && (
              <>
                {principalEventPayload.truckingCompanyType && !isEmpty(principalEventPayload.truckingCompanyType)
                  ? principalEventPayload.truckingCompanyType + ': '
                  : null}
                {principalEventPayload.insuredCommercialName ??
                  [principalEventPayload.insuredFirstName, principalEventPayload.insuredLastName].filter(Boolean).join(' ')}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.SERVICEREQUESTCERTIFICATE_OF_DELETE:
      case VendorPortalEvents.SERVICEREQUESTCERTIFICATE_OF_INSERT:
      case VendorPortalEvents.SERVICEREQUESTCERTIFICATE_OF_UPDATE:
        let serviceRequestCertificateOfEventPayload = (props.userActivityEvent.payloadAfter || {}) as ServiceRequestCertificateOfEventPayload;
        description = (
          <div>
            {serviceRequestCertificateOfEventPayload && (
              <>
                {serviceRequestCertificateOfEventPayload.status && (
                  <>
                    Status: {serviceRequestCertificateOfEventPayload.status}
                    <br />
                  </>
                )}
                {serviceRequestCertificateOfEventPayload.customType && (
                  <>
                    Type: {serviceRequestCertificateOfEventPayload.customType}
                    <br />
                  </>
                )}
                {serviceRequestCertificateOfEventPayload.holderName && (
                  <>
                    Holder: {serviceRequestCertificateOfEventPayload.holderName}
                    <br />
                  </>
                )}
                {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
                  (serviceRequestCertificateOfEventPayload.insuredCommercialName ||
                    serviceRequestCertificateOfEventPayload.insuredFirstName ||
                    serviceRequestCertificateOfEventPayload.insuredLastName) && (
                    <>
                      {serviceRequestCertificateOfEventPayload.truckingCompanyType &&
                      !isEmpty(serviceRequestCertificateOfEventPayload.truckingCompanyType)
                        ? serviceRequestCertificateOfEventPayload.truckingCompanyType + ': '
                        : null}
                      {serviceRequestCertificateOfEventPayload.insuredCommercialName ??
                        [serviceRequestCertificateOfEventPayload.insuredFirstName, serviceRequestCertificateOfEventPayload.insuredLastName]
                          .filter(Boolean)
                          .join(' ')}
                      <br />
                    </>
                  )}
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.OTHERCLAIM_DELETE:
      case VendorPortalEvents.OTHERCLAIM_INSERT:
      case VendorPortalEvents.OTHERCLAIM_UPDATE:
      case VendorPortalEvents.AUTOMOBILELOSSCLAIM_DELETE:
      case VendorPortalEvents.AUTOMOBILELOSSCLAIM_INSERT:
      case VendorPortalEvents.AUTOMOBILELOSSCLAIM_UPDATE:
      case VendorPortalEvents.GENERALLIABILITYCLAIM_DELETE:
      case VendorPortalEvents.GENERALLIABILITYCLAIM_INSERT:
      case VendorPortalEvents.GENERALLIABILITYCLAIM_UPDATE:
      case VendorPortalEvents.PROPERTYLOSSCLAIM_DELETE:
      case VendorPortalEvents.PROPERTYLOSSCLAIM_INSERT:
      case VendorPortalEvents.PROPERTYLOSSCLAIM_UPDATE:
      case VendorPortalEvents.WORKERCOMPENSATIONCLAIM_DELETE:
      case VendorPortalEvents.WORKERCOMPENSATIONCLAIM_INSERT:
      case VendorPortalEvents.WORKERCOMPENSATIONCLAIM_UPDATE:
        let otherClaimEventPayload = (props.userActivityEvent.payloadAfter || {}) as
          | OtherClaimEventPayload
          | AutomobileLossClaimEventPayload
          | GeneralLiabilityClaimEventPayload
          | PropertyLossClaimEventPayload
          | WorkerCompensationClaimEventPayload;
        description = (
          <div>
            {otherClaimEventPayload.claimNumber && (
              <>
                Claim Number: {otherClaimEventPayload.claimNumber}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (otherClaimEventPayload.insuredCommercialName || otherClaimEventPayload.insuredFirstName || otherClaimEventPayload.insuredLastName) && (
                <>
                  {otherClaimEventPayload.truckingCompanyType && !isEmpty(otherClaimEventPayload.truckingCompanyType)
                    ? otherClaimEventPayload.truckingCompanyType + ': '
                    : null}
                  {otherClaimEventPayload.insuredCommercialName ??
                    [otherClaimEventPayload.insuredFirstName, otherClaimEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.TAGAPPLY_DELETE:
      case VendorPortalEvents.TAGAPPLY_INSERT:
      case VendorPortalEvents.TAGAPPLY_UPDATE:
        let tagApplyEventPayload = (props.userActivityEvent.payloadAfter || {}) as TagApplyEventPayload;
        description = (
          <div>
            {tagApplyEventPayload.tagName && (
              <>
                Tag: {tagApplyEventPayload.tagName}
                <br />
              </>
            )}
            {tagApplyEventPayload.tagDescription && (
              <>
                Description: {tagApplyEventPayload.tagDescription}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (tagApplyEventPayload.insuredCommercialName || tagApplyEventPayload.insuredFirstName || tagApplyEventPayload.insuredLastName) && (
                <>
                  {tagApplyEventPayload.truckingCompanyType && !isEmpty(tagApplyEventPayload.truckingCompanyType)
                    ? tagApplyEventPayload.truckingCompanyType + ': '
                    : null}
                  {tagApplyEventPayload.insuredCommercialName ??
                    [tagApplyEventPayload.insuredFirstName, tagApplyEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.TAGAPPLYONPOLICY_DELETE:
      case VendorPortalEvents.TAGAPPLYONPOLICY_INSERT:
      case VendorPortalEvents.TAGAPPLYONPOLICY_UPDATE:
        let tagApplyOnPolicyEventPayload = (props.userActivityEvent.payloadAfter || {}) as TagApplyOnPolicyEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && tagApplyOnPolicyEventPayload.policyNumber && (
              <>
                Policy: {tagApplyOnPolicyEventPayload.policyNumber}
                <br />
              </>
            )}
            {tagApplyOnPolicyEventPayload.tagName && (
              <>
                Tag: {tagApplyOnPolicyEventPayload.tagName}
                <br />
              </>
            )}
            {tagApplyOnPolicyEventPayload.tagDescription && (
              <>
                Description: {tagApplyOnPolicyEventPayload.tagDescription}
                <br />
              </>
            )}
            <br />
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (tagApplyOnPolicyEventPayload.insuredCommercialName ||
                tagApplyOnPolicyEventPayload.insuredFirstName ||
                tagApplyOnPolicyEventPayload.insuredLastName) && (
                <>
                  {tagApplyOnPolicyEventPayload.truckingCompanyType && !isEmpty(tagApplyOnPolicyEventPayload.truckingCompanyType)
                    ? tagApplyOnPolicyEventPayload.truckingCompanyType + ': '
                    : null}
                  {tagApplyOnPolicyEventPayload.insuredCommercialName ??
                    [tagApplyOnPolicyEventPayload.insuredFirstName, tagApplyOnPolicyEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.XDate_DELETE:
      case VendorPortalEvents.XDate_INSERT:
      case VendorPortalEvents.XDate_UPDATE:
        let xDateEventPayload = (props.userActivityEvent.payloadAfter || {}) as XDateEventPayload;
        description = (
          <div>
            {xDateEventPayload.xDate && (
              <>
                X-Date: {convertToUserTimeZone(xDateEventPayload.xDate)} {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {!xDateEventPayload.xDate && (
              <>
                <span style={{ color: '#888' }}>X-Date: None</span>
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (xDateEventPayload.insuredCommercialName || xDateEventPayload.insuredFirstName || xDateEventPayload.insuredLastName) && (
                <>
                  {xDateEventPayload.truckingCompanyType && !isEmpty(xDateEventPayload.truckingCompanyType)
                    ? xDateEventPayload.truckingCompanyType + ': '
                    : null}
                  {xDateEventPayload.insuredCommercialName ??
                    [xDateEventPayload.insuredFirstName, xDateEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.PENDINGCANCELLATION_DELETE:
      case VendorPortalEvents.PENDINGCANCELLATION_INSERT:
      case VendorPortalEvents.PENDINGCANCELLATION_UPDATE:
        let pendingCancellationEventPayload = (props.userActivityEvent.payloadAfter || {}) as PendingCancellationEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && pendingCancellationEventPayload.policyNumber && (
              <>
                Policy: {pendingCancellationEventPayload.policyNumber}
                <br />
              </>
            )}
            {pendingCancellationEventPayload.toDate && (
              <>
                To: {pendingCancellationEventPayload.toDate ? convertToUserTimeZone(pendingCancellationEventPayload.toDate) : ''}
                {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (pendingCancellationEventPayload.insuredCommercialName ||
                pendingCancellationEventPayload.insuredFirstName ||
                pendingCancellationEventPayload.insuredLastName) && (
                <>
                  {pendingCancellationEventPayload.truckingCompanyType && !isEmpty(pendingCancellationEventPayload.truckingCompanyType)
                    ? pendingCancellationEventPayload.truckingCompanyType + ': '
                    : null}
                  {pendingCancellationEventPayload.insuredCommercialName ??
                    [pendingCancellationEventPayload.insuredFirstName, pendingCancellationEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.FINANCE_AGREEMENT_DELETE:
      case VendorPortalEvents.FINANCE_AGREEMENT_INSERT:
      case VendorPortalEvents.FINANCE_AGREEMENT_UPDATE:
        let financeAgreementEventPayload = (props.userActivityEvent.payloadAfter || {}) as FinanceAgreementEventPayload;
        description = (
          <div>
            {financeAgreementEventPayload.contractNumber && (
              <>
                Contract Number: {financeAgreementEventPayload.contractNumber} <br />
              </>
            )}
            {financeAgreementEventPayload.description && (
              <>
                Description: {financeAgreementEventPayload.description}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (financeAgreementEventPayload.insuredCommercialName ||
                financeAgreementEventPayload.insuredFirstName ||
                financeAgreementEventPayload.insuredLastName) && (
                <>
                  {financeAgreementEventPayload.truckingCompanyType && !isEmpty(financeAgreementEventPayload.truckingCompanyType)
                    ? financeAgreementEventPayload.truckingCompanyType + ': '
                    : null}
                  {financeAgreementEventPayload.insuredCommercialName ??
                    [financeAgreementEventPayload.insuredFirstName, financeAgreementEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.MEDICALHEALTHCLAIM_DELETE:
      case VendorPortalEvents.MEDICALHEALTHCLAIM_INSERT:
      case VendorPortalEvents.MEDICALHEALTHCLAIM_UPDATE:
        let medicalHealthClaimEventPayload = (props.userActivityEvent.payloadAfter || {}) as MedicalHealthClaimEventPayload;
        description = (
          <div>
            {medicalHealthClaimEventPayload?.claimNumber && (
              <>
                Claim Number: {medicalHealthClaimEventPayload.claimNumber}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany && medicalHealthClaimEventPayload && (
              <>
                {medicalHealthClaimEventPayload.truckingCompanyType && !isEmpty(medicalHealthClaimEventPayload.truckingCompanyType)
                  ? medicalHealthClaimEventPayload.truckingCompanyType + ': '
                  : null}
                {medicalHealthClaimEventPayload.insuredCommercialName ??
                  [medicalHealthClaimEventPayload.insuredFirstName, medicalHealthClaimEventPayload.insuredLastName].filter(Boolean).join(' ')}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.HISTORICAL_CERTIFICATE_HOLDER_DELETE:
      case VendorPortalEvents.HISTORICAL_CERTIFICATE_HOLDER_INSERT:
      case VendorPortalEvents.HISTORICAL_CERTIFICATE_HOLDER_UPDATE:
        let historicalCertificateHolderEventPayload = (props.userActivityEvent.payloadAfter || {}) as HistoricalCertificateHolderEventPayload;
        description = (
          <div>
            {historicalCertificateHolderEventPayload?.name && (
              <>
                Name: {historicalCertificateHolderEventPayload.name}
                <br />
              </>
            )}
            {historicalCertificateHolderEventPayload?.certificateName && (
              <>
                Certificate: {historicalCertificateHolderEventPayload.certificateName}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany && historicalCertificateHolderEventPayload && (
              <>
                {historicalCertificateHolderEventPayload.truckingCompanyType && !isEmpty(historicalCertificateHolderEventPayload.truckingCompanyType)
                  ? historicalCertificateHolderEventPayload.truckingCompanyType + ': '
                  : null}
                {historicalCertificateHolderEventPayload.insuredCommercialName ??
                  [historicalCertificateHolderEventPayload.insuredFirstName, historicalCertificateHolderEventPayload.insuredLastName]
                    .filter(Boolean)
                    .join(' ')}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.CERTIFICATE_HOLDER_DELETE:
      case VendorPortalEvents.CERTIFICATE_HOLDER_INSERT:
      case VendorPortalEvents.CERTIFICATE_HOLDER_UPDATE:
        let certificateHolderEventPayload = (props.userActivityEvent.payloadAfter || {}) as CertificateHolderEventPayload;
        description = (
          <div>
            {certificateHolderEventPayload?.name && (
              <>
                Name: {certificateHolderEventPayload.name}
                <br />
              </>
            )}
            {(certificateHolderEventPayload?.addressLine1 ||
              certificateHolderEventPayload?.addressLine2 ||
              certificateHolderEventPayload?.city ||
              certificateHolderEventPayload?.stateName ||
              certificateHolderEventPayload?.zipCode) && (
              <>
                Address: {certificateHolderEventPayload?.addressLine1}
                {certificateHolderEventPayload?.addressLine2 && `, ${certificateHolderEventPayload.addressLine2}`}
                {certificateHolderEventPayload?.city && `, ${certificateHolderEventPayload.city}`}
                {certificateHolderEventPayload?.stateName && `, ${certificateHolderEventPayload.stateName}`}
                {certificateHolderEventPayload?.zipCode && ` ${certificateHolderEventPayload.zipCode}`}
                <br />
              </>
            )}
            {certificateHolderEventPayload?.phone && (
              <>
                Phone: {certificateHolderEventPayload.phone}
                <br />
              </>
            )}
            {certificateHolderEventPayload?.eMail && (
              <>
                Email: {certificateHolderEventPayload.eMail}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.TASK_CATEGORY_DELETE:
      case VendorPortalEvents.TASK_CATEGORY_INSERT:
      case VendorPortalEvents.TASK_CATEGORY_UPDATE:
        let taskCategoryEventPayload = (props.userActivityEvent.payloadAfter || {}) as TaskCategoryEventPayload;
        description = (
          <div>
            {taskCategoryEventPayload?.name && (
              <>
                Name: {taskCategoryEventPayload.name}
                <br />
              </>
            )}
            {taskCategoryEventPayload?.description && (
              <>
                Description: {taskCategoryEventPayload.description}
                <br />
              </>
            )}
            {taskCategoryEventPayload?.defaultStatus && (
              <>
                Default Status: {taskCategoryEventPayload.defaultStatus}
                <br />
              </>
            )}
            {taskCategoryEventPayload?.defaultPriority && (
              <>
                Default Priority: {taskCategoryEventPayload.defaultPriority}
                <br />
              </>
            )}
            {taskCategoryEventPayload?.supervisorName && (
              <>
                Supervisor: {taskCategoryEventPayload.supervisorName}
                <br />
              </>
            )}
            {taskCategoryEventPayload?.assigneeName && (
              <>
                Assignee: {taskCategoryEventPayload.assigneeName}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.LOB_DELETE:
      case VendorPortalEvents.LOB_INSERT:
      case VendorPortalEvents.LOB_UPDATE:
        let lOBEventPayload = (props.userActivityEvent.payloadAfter || {}) as LOBEventPayload;
        description = (
          <div>
            {lOBEventPayload?.name && (
              <>
                Name: {lOBEventPayload.name}
                <br />
              </>
            )}
            {lOBEventPayload?.lOBClassName && (
              <>
                LOB Class: {lOBEventPayload.lOBClassName}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.OPPORTUNITYSTAGE_DELETE:
      case VendorPortalEvents.OPPORTUNITYSTAGE_INSERT:
      case VendorPortalEvents.OPPORTUNITYSTAGE_UPDATE:
        let opportunityStageEventPayload = (props.userActivityEvent.payloadAfter || {}) as OpportunityStageEventPayload;
        description = (
          <div>
            {opportunityStageEventPayload?.name && (
              <>
                Name: {opportunityStageEventPayload.name}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.INSURANCEAGENCY_DELETE:
      case VendorPortalEvents.INSURANCEAGENCY_INSERT:
      case VendorPortalEvents.INSURANCEAGENCY_UPDATE:
        let insuranceAgencyEventPayload = (props.userActivityEvent.payloadAfter || {}) as InsuranceAgencyEventPayload;
        description = (
          <div>
            {insuranceAgencyEventPayload?.name && (
              <>
                Name: {insuranceAgencyEventPayload.name}
                <br />
              </>
            )}
            {(insuranceAgencyEventPayload?.addressLine1 || insuranceAgencyEventPayload?.addressLine2) && (
              <>
                Address: {insuranceAgencyEventPayload.addressLine1}
                {insuranceAgencyEventPayload.addressLine2 && `, ${insuranceAgencyEventPayload.addressLine2}`}
                <br />
              </>
            )}
            {insuranceAgencyEventPayload?.phone && (
              <>
                Phone: {insuranceAgencyEventPayload.phone}
                <br />
              </>
            )}
            {insuranceAgencyEventPayload?.email && (
              <>
                Email: {insuranceAgencyEventPayload.email}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.AGENT_DELETE:
      case VendorPortalEvents.AGENT_INSERT:
      case VendorPortalEvents.AGENT_UPDATE:
        let agentEventPayload = (props.userActivityEvent.payloadAfter || {}) as AgentEventPayload;
        description = (
          <div>
            {agentEventPayload && (
              <>
                {((agentEventPayload.firstName && agentEventPayload.firstName.trim()) ||
                  (agentEventPayload.lastName && agentEventPayload.lastName.trim())) && (
                  <>
                    Name: {[agentEventPayload.firstName, agentEventPayload.lastName].filter(Boolean).join(' ')}
                    <br />
                  </>
                )}
                {agentEventPayload.email && (
                  <>
                    Email: {agentEventPayload.email}
                    <br />
                  </>
                )}
                {agentEventPayload.phone && (
                  <>
                    Phone: {agentEventPayload.phone}
                    <br />
                  </>
                )}
                {agentEventPayload.agentType && (
                  <>
                    Agent Type: {agentEventPayload.agentType}
                    <br />
                  </>
                )}
                {agentEventPayload.primaryRole && (
                  <>
                    Primary Role: {agentEventPayload.primaryRole}
                    <br />
                  </>
                )}
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.TAG_DELETE:
      case VendorPortalEvents.TAG_INSERT:
      case VendorPortalEvents.TAG_UPDATE:
        let tagEventPayload = (props.userActivityEvent.payloadAfter || {}) as TagEventPayload;
        description = (
          <div>
            {tagEventPayload?.name && (
              <>
                Name: {tagEventPayload.name}
                <br />
              </>
            )}
            {tagEventPayload?.description && (
              <>
                Description: {tagEventPayload.description}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.OPPORTUNITY_TRACKING_KEYS_DELETE:
      case VendorPortalEvents.OPPORTUNITY_TRACKING_KEYS_INSERT:
      case VendorPortalEvents.OPPORTUNITY_TRACKING_KEYS_UPDATE:
        let opportunityTrackingKeysEventPayload = (props.userActivityEvent.payloadAfter || {}) as OpportunityTrackingKeysEventPayload;
        description = (
          <div>
            {opportunityTrackingKeysEventPayload?.referralType && (
              <>
                Referral Type: {opportunityTrackingKeysEventPayload.referralType}
                <br />
              </>
            )}
            {opportunityTrackingKeysEventPayload?.referralName && (
              <>
                Referral Name: {opportunityTrackingKeysEventPayload.referralName}
                <br />
              </>
            )}
            {opportunityTrackingKeysEventPayload?.status && (
              <>
                Status: {opportunityTrackingKeysEventPayload.status}
                <br />
              </>
            )}
            {opportunityTrackingKeysEventPayload?.comments && (
              <>
                Comments: {opportunityTrackingKeysEventPayload.comments}
                <br />
              </>
            )}
            {opportunityTrackingKeysEventPayload?.lastChangeUserName && <>Changed by {opportunityTrackingKeysEventPayload.lastChangeUserName}</>}
          </div>
        );
        break;

      case VendorPortalEvents.DIRECT_BILL_AMOUNT_DELETE:
      case VendorPortalEvents.DIRECT_BILL_AMOUNT_INSERT:
      case VendorPortalEvents.DIRECT_BILL_AMOUNT_UPDATE:
        let directBillAmountEventPayload = (props.userActivityEvent.payloadAfter || {}) as DirectBillAmountEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && directBillAmountEventPayload.policyNumber && (
              <>
                Policy: {directBillAmountEventPayload.policyNumber}
                <br />
              </>
            )}
            {directBillAmountEventPayload.amountValue && (
              <>
                Amount: {directBillAmountEventPayload.amountValue}
                <br />
              </>
            )}
            {directBillAmountEventPayload.description && (
              <>
                Description: {directBillAmountEventPayload.description}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (directBillAmountEventPayload.insuredCommercialName ||
                directBillAmountEventPayload.insuredFirstName ||
                directBillAmountEventPayload.insuredLastName) && (
                <>
                  {directBillAmountEventPayload.truckingCompanyType && !isEmpty(directBillAmountEventPayload.truckingCompanyType)
                    ? directBillAmountEventPayload.truckingCompanyType + ': '
                    : null}
                  {directBillAmountEventPayload.insuredCommercialName ??
                    [directBillAmountEventPayload.insuredFirstName, directBillAmountEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.DIRECT_BILL_AMOUNT_PAYMENT_DELETE:
      case VendorPortalEvents.DIRECT_BILL_AMOUNT_PAYMENT_INSERT:
      case VendorPortalEvents.DIRECT_BILL_AMOUNT_PAYMENT_UPDATE:
        const directBillAmountPaymentEventPayload = (props.userActivityEvent.payloadAfter || {}) as DirectBillAmountPaymentEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && directBillAmountPaymentEventPayload.policyNumber && (
              <>
                Policy: {directBillAmountPaymentEventPayload.policyNumber}
                <br />
              </>
            )}
            {directBillAmountPaymentEventPayload.amountReceivedDate && (
              <>
                Amount Received Date: {convertToUserTimeZone(directBillAmountPaymentEventPayload.amountReceivedDate)}{' '}
                {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {directBillAmountPaymentEventPayload.receivedAmount && (
              <>
                Received Amount: {directBillAmountPaymentEventPayload.receivedAmount}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (directBillAmountPaymentEventPayload.insuredCommercialName ||
                directBillAmountPaymentEventPayload.insuredFirstName ||
                directBillAmountPaymentEventPayload.insuredLastName) && (
                <>
                  {directBillAmountPaymentEventPayload.truckingCompanyType && !isEmpty(directBillAmountPaymentEventPayload.truckingCompanyType)
                    ? directBillAmountPaymentEventPayload.truckingCompanyType + ': '
                    : null}
                  {directBillAmountPaymentEventPayload.insuredCommercialName ??
                    [directBillAmountPaymentEventPayload.insuredFirstName, directBillAmountPaymentEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.INSURED_INVOICE_INSERT:
      case VendorPortalEvents.INSURED_INVOICE_UPDATE:
        let insuredInvoiceEventPayload = (props.userActivityEvent.payloadAfter || {}) as InsuredInvoiceEventPayload;
        description = (
          <div>
            {insuredInvoiceEventPayload?.number && (
              <>
                Invoice Number: {insuredInvoiceEventPayload.number}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.Policy &&
              insuredInvoiceEventPayload?.policyNumberList &&
              insuredInvoiceEventPayload.policyNumberList.length > 0 && (
                <>
                  Policies: {insuredInvoiceEventPayload.policyNumberList.join(', ')}
                  <br />
                </>
              )}
            {insuredInvoiceEventPayload?.dueDate && (
              <>
                Due: {convertToUserTimeZone(insuredInvoiceEventPayload.dueDate)} {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {insuredInvoiceEventPayload?.description && (
              <>
                Description: {insuredInvoiceEventPayload.description}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany && insuredInvoiceEventPayload && (
              <>
                {insuredInvoiceEventPayload.truckingCompanyType && !isEmpty(insuredInvoiceEventPayload.truckingCompanyType)
                  ? insuredInvoiceEventPayload.truckingCompanyType + ': '
                  : null}
                {insuredInvoiceEventPayload.insuredCommercialName ??
                  [insuredInvoiceEventPayload.insuredFirstName, insuredInvoiceEventPayload.insuredLastName].filter(Boolean).join(' ')}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.FLOOD_COVERAGE_DELETE:
      case VendorPortalEvents.FLOOD_COVERAGE_INSERT:
      case VendorPortalEvents.FLOOD_COVERAGE_UPDATE:
        let floodCoverageEventPayload = (props.userActivityEvent.payloadAfter || {}) as FloodCoverageEventPayload;
        description = (
          <div>
            {floodCoverageEventPayload?.floodCoverageName && (
              <>
                Coverage: {floodCoverageEventPayload.floodCoverageName}
                <br />
              </>
            )}
            {floodCoverageEventPayload?.buildingName && (
              <>
                Building: {floodCoverageEventPayload.buildingName}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.Policy && floodCoverageEventPayload?.primaryPolicy && (
              <>
                Policy: {floodCoverageEventPayload.primaryPolicy.policyNumber}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany && floodCoverageEventPayload && (
              <>
                {floodCoverageEventPayload.truckingCompanyType && !isEmpty(floodCoverageEventPayload.truckingCompanyType)
                  ? floodCoverageEventPayload.truckingCompanyType + ': '
                  : null}
                {floodCoverageEventPayload.insuredCommercialName ??
                  [floodCoverageEventPayload.insuredFirstName, floodCoverageEventPayload.insuredLastName].filter(Boolean).join(' ')}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_RECEIVABLE_DELETE:
      case VendorPortalEvents.ENDORSEMENT_RECEIVABLE_INSERT:
      case VendorPortalEvents.ENDORSEMENT_RECEIVABLE_UPDATE:
        const endorsementReceivableEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementReceivableEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementReceivableEventPayload.policyNumber && (
              <>
                Policy: {endorsementReceivableEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementReceivableEventPayload.dueDate && (
              <>
                Due Date: {convertToUserTimeZone(endorsementReceivableEventPayload.dueDate)} {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementReceivableEventPayload.insuredCommercialName ||
                endorsementReceivableEventPayload.insuredFirstName ||
                endorsementReceivableEventPayload.insuredLastName) && (
                <>
                  {endorsementReceivableEventPayload.truckingCompanyType && !isEmpty(endorsementReceivableEventPayload.truckingCompanyType)
                    ? endorsementReceivableEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementReceivableEventPayload.insuredCommercialName ??
                    [endorsementReceivableEventPayload.insuredFirstName, endorsementReceivableEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_RECEIVABLE_PAYMENT_DELETE:
      case VendorPortalEvents.ENDORSEMENT_RECEIVABLE_PAYMENT_INSERT:
      case VendorPortalEvents.ENDORSEMENT_RECEIVABLE_PAYMENT_UPDATE:
        const endorsementReceivablePaymentEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementReceivablePaymentEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementReceivablePaymentEventPayload.policyNumber && (
              <>
                Policy: {endorsementReceivablePaymentEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementReceivablePaymentEventPayload.commissionReceivedDate && (
              <>
                Commission received date: {convertToUserTimeZone(endorsementReceivablePaymentEventPayload.commissionReceivedDate)}{' '}
                {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementReceivablePaymentEventPayload.insuredCommercialName ||
                endorsementReceivablePaymentEventPayload.insuredFirstName ||
                endorsementReceivablePaymentEventPayload.insuredLastName) && (
                <>
                  {endorsementReceivablePaymentEventPayload.truckingCompanyType &&
                  !isEmpty(endorsementReceivablePaymentEventPayload.truckingCompanyType)
                    ? endorsementReceivablePaymentEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementReceivablePaymentEventPayload.insuredCommercialName ??
                    [endorsementReceivablePaymentEventPayload.insuredFirstName, endorsementReceivablePaymentEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_PAYABLE_DELETE:
      case VendorPortalEvents.ENDORSEMENT_PAYABLE_INSERT:
      case VendorPortalEvents.ENDORSEMENT_PAYABLE_UPDATE:
        const endorsementPayableEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementPayableEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementPayableEventPayload.policyNumber && (
              <>
                Policy: {endorsementPayableEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementPayableEventPayload.dueDate && (
              <>
                Due Date:
                {convertToUserTimeZone(endorsementPayableEventPayload.dueDate)} {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementPayableEventPayload.insuredCommercialName ||
                endorsementPayableEventPayload.insuredFirstName ||
                endorsementPayableEventPayload.insuredLastName) && (
                <>
                  {endorsementPayableEventPayload.truckingCompanyType && !isEmpty(endorsementPayableEventPayload.truckingCompanyType)
                    ? endorsementPayableEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementPayableEventPayload.insuredCommercialName ??
                    [endorsementPayableEventPayload.insuredFirstName, endorsementPayableEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_PAYABLE_PAYMENT_DELETE:
      case VendorPortalEvents.ENDORSEMENT_PAYABLE_PAYMENT_INSERT:
      case VendorPortalEvents.ENDORSEMENT_PAYABLE_PAYMENT_UPDATE:
        const endorsementPayablePaymentEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementPayablePaymentEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementPayablePaymentEventPayload.policyNumber && (
              <>
                Policy: {endorsementPayablePaymentEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementPayablePaymentEventPayload.commissionReceivedDate && (
              <>
                Commission Received Date: {convertToUserTimeZone(endorsementPayablePaymentEventPayload.commissionReceivedDate)}{' '}
                {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementPayablePaymentEventPayload.insuredCommercialName ||
                endorsementPayablePaymentEventPayload.insuredFirstName ||
                endorsementPayablePaymentEventPayload.insuredLastName) && (
                <>
                  {endorsementPayablePaymentEventPayload.truckingCompanyType && !isEmpty(endorsementPayablePaymentEventPayload.truckingCompanyType)
                    ? endorsementPayablePaymentEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementPayablePaymentEventPayload.insuredCommercialName ??
                    [endorsementPayablePaymentEventPayload.insuredFirstName, endorsementPayablePaymentEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_FINANCING_DELETE:
      case VendorPortalEvents.ENDORSEMENT_FINANCING_INSERT:
      case VendorPortalEvents.ENDORSEMENT_FINANCING_UPDATE:
        const endorsementFinancingEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementFinancingEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementFinancingEventPayload.policyNumber && (
              <>
                Policy: {endorsementFinancingEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementFinancingEventPayload.description && (
              <>
                Description: {endorsementFinancingEventPayload.description}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementFinancingEventPayload.insuredCommercialName ||
                endorsementFinancingEventPayload.insuredFirstName ||
                endorsementFinancingEventPayload.insuredLastName) && (
                <>
                  {endorsementFinancingEventPayload.truckingCompanyType && !isEmpty(endorsementFinancingEventPayload.truckingCompanyType)
                    ? endorsementFinancingEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementFinancingEventPayload.insuredCommercialName ??
                    [endorsementFinancingEventPayload.insuredFirstName, endorsementFinancingEventPayload.insuredLastName].filter(Boolean).join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_FINANCING_PAYMENT_DELETE:
      case VendorPortalEvents.ENDORSEMENT_FINANCING_PAYMENT_INSERT:
      case VendorPortalEvents.ENDORSEMENT_FINANCING_PAYMENT_UPDATE:
        const endorsementFinancingPaymentEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementFinancingPaymentEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementFinancingPaymentEventPayload.policyNumber && (
              <>
                Policy: {endorsementFinancingPaymentEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementFinancingPaymentEventPayload.commissionReceivedDate && (
              <>
                Commission Received Date: {convertToUserTimeZone(endorsementFinancingPaymentEventPayload.commissionReceivedDate)}{' '}
                {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementFinancingPaymentEventPayload.insuredCommercialName ||
                endorsementFinancingPaymentEventPayload.insuredFirstName ||
                endorsementFinancingPaymentEventPayload.insuredLastName) && (
                <>
                  {endorsementFinancingPaymentEventPayload.truckingCompanyType &&
                  !isEmpty(endorsementFinancingPaymentEventPayload.truckingCompanyType)
                    ? endorsementFinancingPaymentEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementFinancingPaymentEventPayload.insuredCommercialName ??
                    [endorsementFinancingPaymentEventPayload.insuredFirstName, endorsementFinancingPaymentEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_FINANCING_INCENTIVE_DELETE:
      case VendorPortalEvents.ENDORSEMENT_FINANCING_INCENTIVE_INSERT:
      case VendorPortalEvents.ENDORSEMENT_FINANCING_INCENTIVE_UPDATE:
        const endorsementFinancingIncentiveEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementFinancingIncentiveEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementFinancingIncentiveEventPayload.policyNumber && (
              <>
                Policy: {endorsementFinancingIncentiveEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementFinancingIncentiveEventPayload.dueDate && (
              <>
                Due Date: {convertToUserTimeZone(endorsementFinancingIncentiveEventPayload.dueDate)} {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {endorsementFinancingIncentiveEventPayload.agencyCommissionTypeText && (
              <>
                Agency Commission Type: {endorsementFinancingIncentiveEventPayload.agencyCommissionTypeText}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementFinancingIncentiveEventPayload.insuredCommercialName ||
                endorsementFinancingIncentiveEventPayload.insuredFirstName ||
                endorsementFinancingIncentiveEventPayload.insuredLastName) && (
                <>
                  {endorsementFinancingIncentiveEventPayload.truckingCompanyType &&
                  !isEmpty(endorsementFinancingIncentiveEventPayload.truckingCompanyType)
                    ? endorsementFinancingIncentiveEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementFinancingIncentiveEventPayload.insuredCommercialName ??
                    [endorsementFinancingIncentiveEventPayload.insuredFirstName, endorsementFinancingIncentiveEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_FINANCING_INCENTIVE_PAYMENT_DELETE:
      case VendorPortalEvents.ENDORSEMENT_FINANCING_INCENTIVE_PAYMENT_INSERT:
      case VendorPortalEvents.ENDORSEMENT_FINANCING_INCENTIVE_PAYMENT_UPDATE:
        const endorsementFinancingIncentivePaymentEventPayload = (props.userActivityEvent.payloadAfter ||
          {}) as EndorsementFinancingIncentivePaymentEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementFinancingIncentivePaymentEventPayload.policyNumber && (
              <>
                Policy: {endorsementFinancingIncentivePaymentEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementFinancingIncentivePaymentEventPayload.incentiveFeeReceivedDate && (
              <>
                Incentive Fee Received Date: {convertToUserTimeZone(endorsementFinancingIncentivePaymentEventPayload.incentiveFeeReceivedDate)}{' '}
                {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {endorsementFinancingIncentivePaymentEventPayload.incentiveFeeReceivedAmount && (
              <>
                Incentive Fee Received Amount: {endorsementFinancingIncentivePaymentEventPayload.incentiveFeeReceivedAmount}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementFinancingIncentivePaymentEventPayload.insuredCommercialName ||
                endorsementFinancingIncentivePaymentEventPayload.insuredFirstName ||
                endorsementFinancingIncentivePaymentEventPayload.insuredLastName) && (
                <>
                  {endorsementFinancingIncentivePaymentEventPayload.truckingCompanyType &&
                  !isEmpty(endorsementFinancingIncentivePaymentEventPayload.truckingCompanyType)
                    ? endorsementFinancingIncentivePaymentEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementFinancingIncentivePaymentEventPayload.insuredCommercialName ??
                    [
                      endorsementFinancingIncentivePaymentEventPayload.insuredFirstName,
                      endorsementFinancingIncentivePaymentEventPayload.insuredLastName,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_AGENTS_COMMISSION_PAYMENT_DELETE:
      case VendorPortalEvents.ENDORSEMENT_AGENTS_COMMISSION_PAYMENT_INSERT:
      case VendorPortalEvents.ENDORSEMENT_AGENTS_COMMISSION_PAYMENT_UPDATE:
        const endorsementAgentsCommissionPaymentEventPayload = (props.userActivityEvent.payloadAfter ||
          {}) as EndorsementAgentsCommissionPaymentEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementAgentsCommissionPaymentEventPayload.policyNumber && (
              <>
                Policy: {endorsementAgentsCommissionPaymentEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementAgentsCommissionPaymentEventPayload.commissionReceivedDate && (
              <>
                Commission Received Date: {convertToUserTimeZone(endorsementAgentsCommissionPaymentEventPayload.commissionReceivedDate)}{' '}
                {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {endorsementAgentsCommissionPaymentEventPayload.commissionReceivedAmount && (
              <>
                Commission Received Amount: {endorsementAgentsCommissionPaymentEventPayload.commissionReceivedAmount}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementAgentsCommissionPaymentEventPayload.insuredCommercialName ||
                endorsementAgentsCommissionPaymentEventPayload.insuredFirstName ||
                endorsementAgentsCommissionPaymentEventPayload.insuredLastName) && (
                <>
                  {endorsementAgentsCommissionPaymentEventPayload.truckingCompanyType &&
                  !isEmpty(endorsementAgentsCommissionPaymentEventPayload.truckingCompanyType)
                    ? endorsementAgentsCommissionPaymentEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementAgentsCommissionPaymentEventPayload.insuredCommercialName ??
                    [endorsementAgentsCommissionPaymentEventPayload.insuredFirstName, endorsementAgentsCommissionPaymentEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_AGENCY_COMMISSION_PAYMENT_DELETE:
      case VendorPortalEvents.ENDORSEMENT_AGENCY_COMMISSION_PAYMENT_INSERT:
      case VendorPortalEvents.ENDORSEMENT_AGENCY_COMMISSION_PAYMENT_UPDATE:
        const endorsementAgencyCommissionPaymentEventPayload = (props.userActivityEvent.payloadAfter ||
          {}) as EndorsementAgencyCommissionPaymentEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementAgencyCommissionPaymentEventPayload.policyNumber && (
              <>
                Policy: {endorsementAgencyCommissionPaymentEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementAgencyCommissionPaymentEventPayload.commissionReceivedDate && (
              <>
                Commission Received Date: {convertToUserTimeZone(endorsementAgencyCommissionPaymentEventPayload.commissionReceivedDate)}{' '}
                {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
            {endorsementAgencyCommissionPaymentEventPayload.commissionReceivedAmount && (
              <>
                Commission Received Amount: {endorsementAgencyCommissionPaymentEventPayload.commissionReceivedAmount}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementAgencyCommissionPaymentEventPayload.insuredCommercialName ||
                endorsementAgencyCommissionPaymentEventPayload.insuredFirstName ||
                endorsementAgencyCommissionPaymentEventPayload.insuredLastName) && (
                <>
                  {endorsementAgencyCommissionPaymentEventPayload.truckingCompanyType &&
                  !isEmpty(endorsementAgencyCommissionPaymentEventPayload.truckingCompanyType)
                    ? endorsementAgencyCommissionPaymentEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementAgencyCommissionPaymentEventPayload.insuredCommercialName ??
                    [endorsementAgencyCommissionPaymentEventPayload.insuredFirstName, endorsementAgencyCommissionPaymentEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_AGENT_COMMISSION_DELETE:
      case VendorPortalEvents.ENDORSEMENT_AGENT_COMMISSION_INSERT:
      case VendorPortalEvents.ENDORSEMENT_AGENT_COMMISSION_UPDATE:
        const endorsementAgentCommissionEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementAgentCommissionEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementAgentCommissionEventPayload.policyNumber && (
              <>
                Policy: {endorsementAgentCommissionEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementAgentCommissionEventPayload.agentName && (
              <>
                Agent: {endorsementAgentCommissionEventPayload.agentName}
                <br />
              </>
            )}
            {endorsementAgentCommissionEventPayload.commissionTypeText && (
              <>
                Commission Type: {endorsementAgentCommissionEventPayload.commissionTypeText}
                <br />
              </>
            )}
            {endorsementAgentCommissionEventPayload.commissionValue && (
              <>
                Commission Value: {endorsementAgentCommissionEventPayload.commissionValue}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementAgentCommissionEventPayload.insuredCommercialName ||
                endorsementAgentCommissionEventPayload.insuredFirstName ||
                endorsementAgentCommissionEventPayload.insuredLastName) && (
                <>
                  {endorsementAgentCommissionEventPayload.truckingCompanyType && !isEmpty(endorsementAgentCommissionEventPayload.truckingCompanyType)
                    ? endorsementAgentCommissionEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementAgentCommissionEventPayload.insuredCommercialName ??
                    [endorsementAgentCommissionEventPayload.insuredFirstName, endorsementAgentCommissionEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_AGENCY_COMMISSION_DELETE:
      case VendorPortalEvents.ENDORSEMENT_AGENCY_COMMISSION_INSERT:
      case VendorPortalEvents.ENDORSEMENT_AGENCY_COMMISSION_UPDATE:
        const endorsementAgencyCommissionEventPayload = (props.userActivityEvent.payloadAfter || {}) as EndorsementAgencyCommissionEventPayload;
        description = (
          <div>
            {props.objectType !== EventObjectTypeEnum.Policy && endorsementAgencyCommissionEventPayload.policyNumber && (
              <>
                Policy: {endorsementAgencyCommissionEventPayload.policyNumber}
                <br />
              </>
            )}
            {endorsementAgencyCommissionEventPayload.commissionTypeText && (
              <>
                Commission Type: {endorsementAgencyCommissionEventPayload.commissionTypeText}
                <br />
              </>
            )}
            {endorsementAgencyCommissionEventPayload.agencyCommissionTypeText && (
              <>
                Agency Commission Type: {endorsementAgencyCommissionEventPayload.agencyCommissionTypeText}
                <br />
              </>
            )}
            {endorsementAgencyCommissionEventPayload.commissionValue && (
              <>
                Commission Value: {endorsementAgencyCommissionEventPayload.commissionValue}
                <br />
              </>
            )}
            {props.objectType !== EventObjectTypeEnum.TruckingCompany &&
              (endorsementAgencyCommissionEventPayload.insuredCommercialName ||
                endorsementAgencyCommissionEventPayload.insuredFirstName ||
                endorsementAgencyCommissionEventPayload.insuredLastName) && (
                <>
                  {endorsementAgencyCommissionEventPayload.truckingCompanyType &&
                  !isEmpty(endorsementAgencyCommissionEventPayload.truckingCompanyType)
                    ? endorsementAgencyCommissionEventPayload.truckingCompanyType + ': '
                    : null}
                  {endorsementAgencyCommissionEventPayload.insuredCommercialName ??
                    [endorsementAgencyCommissionEventPayload.insuredFirstName, endorsementAgencyCommissionEventPayload.insuredLastName]
                      .filter(Boolean)
                      .join(' ')}
                  <br />
                </>
              )}
          </div>
        );
        break;

      case VendorPortalEvents.TAGAPPLIESTOOPPORTUNITY_DELETE:
      case VendorPortalEvents.TAGAPPLIESTOOPPORTUNITY_INSERT:
      case VendorPortalEvents.TAGAPPLIESTOOPPORTUNITY_UPDATE:
        const tagAppliesToOpportunityEventPayload = (props.userActivityEvent.payloadAfter || {}) as TagAppliesToOpportunityEventPayload;
        description = (
          <div>
            {tagAppliesToOpportunityEventPayload.tagName && (
              <>
                Tag: {tagAppliesToOpportunityEventPayload.tagName} {tagAppliesToOpportunityEventPayload.tagDescription}
                <br />
              </>
            )}
            {tagAppliesToOpportunityEventPayload.tagCategoryName && (
              <>
                Category: {tagAppliesToOpportunityEventPayload.tagCategoryName}
                <br />
              </>
            )}
            {tagAppliesToOpportunityEventPayload.dateApplied && (
              <>
                Date Applied: {convertToUserTimeZone(tagAppliesToOpportunityEventPayload.dateApplied)} {userTimeZone && <span>({userTimeZone})</span>}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.WORKGROUP_DELETE:
      case VendorPortalEvents.WORKGROUP_INSERT:
      case VendorPortalEvents.WORKGROUP_UPDATE:
        const workgroupEventPayload = (props.userActivityEvent.payloadAfter || {}) as WorkgroupEventPayload;
        description = (
          <div>
            {workgroupEventPayload.name && (
              <>
                Workgroup: {workgroupEventPayload.name}
                <br />
              </>
            )}
            {workgroupEventPayload.active !== undefined && (
              <>
                Status: {workgroupEventPayload.active ? 'Active' : 'Inactive'}
                <br />
              </>
            )}
            {workgroupEventPayload.agents && workgroupEventPayload.agents.length > 0 && (
              <>
                Agents: {workgroupEventPayload.agents.length}
                <br />
              </>
            )}
          </div>
        );
        break;

      case VendorPortalEvents.REFERRAL_SOURCE_CATEGORY_DELETE:
      case VendorPortalEvents.REFERRAL_SOURCE_CATEGORY_INSERT:
      case VendorPortalEvents.REFERRAL_SOURCE_CATEGORY_UPDATE:
        const referralSourceCategoryEventPayload = (props.userActivityEvent.payloadAfter || {}) as ReferralSourceCategoryEventPayload;
        description = (
          <div>
            {referralSourceCategoryEventPayload.name && (
              <>
                Category: {referralSourceCategoryEventPayload.name}
                <br />
              </>
            )}
          </div>
        );
        break;

      default:
        description = <div style={{ color: 'red' }}>NOT IMPLEMENTED!</div>;
        break;
    }

    return description;
  };

  const getEventType = (eventType: string | undefined) => {
    if (!eventType) return 'Unknown Event Type';

    const match = eventType.match(/(Insert|Update|Delete|Changed)$/);
    if (match) {
      let lastWord = match[1];
      const mainText = eventType.slice(0, -lastWord.length);
      let color = '';
      switch (lastWord) {
        case 'Insert':
          color = 'green';
          break;
        case 'Update':
          color = 'orange';
          break;
        case 'Changed':
          lastWord = 'Update';
          color = 'orange';
          break;
        case 'Delete':
          color = 'red';
          break;
        default:
          color = 'black';
      }
      return (
        <>
          {mainText}{' '}
          <Tag color={color} style={{ marginLeft: 4 }}>
            {lastWord}
          </Tag>
        </>
      );
    }

    return 'Unknown Event Type';
  };

  return (
    <div className="user-activity-event-item">
      <Spin spinning={humanReadableEventTypesLoading || userTimeZoneLoading} tip="Loading...">
        <Row>
          <Col flex={'40px'}>{getEventIcon(props.userActivityEvent.eventType)}</Col>
          <Col flex={'auto'}>
            <span>
              {userTimeZone && (
                <>
                  {moment
                    .utc(props.userActivityEvent.eventDateUtc)
                    .tz(userTimeZone || 'America/Chicago')
                    .format('MM/DD/YYYY h:mm:ss A')}
                </>
              )}{' '}
              {userTimeZone && (
                <InfoCircleOutlined
                  title={
                    userTimeZone +
                    ' - ' +
                    moment
                      .utc(props.userActivityEvent.eventDateUtc)
                      .tz(userTimeZone || 'America/Chicago')
                      .format('MM/DD/YYYY h:mm:ss A')
                  }
                />
              )}{' '}
              {' - '}
            </span>
            <b>
              {humanReadableEventTypes &&
                Array.isArray(humanReadableEventTypes) &&
                getEventType(
                  humanReadableEventTypes.find((item: { key: string; value: string }) => item.key === props.userActivityEvent.eventType)?.value,
                )}
            </b>
            <div>{eventDescription()}</div>
          </Col>
          <Col flex={'300px'} style={{ textAlign: 'right' }}>
            <Space align="end" title="User who made the change">
              {(props.userActivityEvent.payloadAfter as any).lastChangedUserName ||
              (props.userActivityEvent.payloadAfter as any).lastChangeUserName ? (
                <>
                  <LucideIcons.User color={token.colorPrimary} size={14} />
                  {(props.userActivityEvent.payloadAfter as any).lastChangedUserName ??
                    (props.userActivityEvent.payloadAfter as any).lastChangeUserName}
                </>
              ) : null}
            </Space>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default UserActivityEventItem;
